<template>
  <div class="d-inline">
    <AppPrint
      ref="printer"
      element-id="#competition-results-print-export-container"
      :button-props="{ size: 'large' }"
      :external-loading="loading"
      :custom-handle="print"
    />
    <div
      id="competition-results-print-export-container"
      class="d-none d-print-block"
      style="width: 100%"
    >
      <template v-if="entityType == 'personId'">
        <div>{{ entityObject.name }}</div>
        <div>
          {{
            $t(
              "people:personPage.FEIID.field",
              "FEI ID:",
              "Field of the FEI ID on person page"
            )
          }}
          {{ entityObject.feiid }}
        </div>
        <div>
          {{
            $t(
              "people:personPage.licencenumber.field",
              "Licence number:",
              "Field of the licence number on person page"
            )
          }}
          {{ entityObject.identifier || entityObject.identifierMasked }}
        </div>
      </template>
      <template v-else-if="entityType == 'horseId'">
        <AppPrintBasicHorseDetails :horse="entityObject" />
      </template>
      <br />

      <div
        v-for="(results, index) in receivedResults"
        :key="index"
        class="mb-2 competition-results-print-export-event-group"
      >
        <div class="pa-2 pl-0 pb-0">
          <b>{{ results[0].competition.event.name }}</b>
          <small class="mx-2">{{
            results[0].competition.event.organiser.name
          }}</small>
        </div>
        <table class="entsys--table">
          <colgroup>
            <col style="width: 40%" />
            <col />
            <col style="width: 200px" />
            <col style="width: 75px" />
            <col style="width: 75px" />
          </colgroup>
          <thead>
            <tr>
              <th class="text-left">
                {{
                  $t(
                    "common:discipline." +
                      results[0].competition.event.discipline.name,
                    results[0].competition.event.discipline.name
                  )
                }}
              </th>

              <th class="text-left">
                <template v-if="personView">
                  {{
                    $t(
                      "events:common.results.horse.column",
                      "Horse",
                      "Label for horse column in results"
                    )
                  }}
                </template>
                <template v-else>
                  {{
                    $t(
                      "events:common.results.athlete.column",
                      "Athlete",
                      "Label for athlete column in results"
                    )
                  }}
                </template>
              </th>
              <th class="text-left">
                {{
                  $t(
                    "events:common.result.column",
                    "Result",
                    "Label for result column in results"
                  )
                }}
              </th>
              <th class="text-left">
                {{
                  $t(
                    "events:common.rank.column",
                    "Rank",
                    "Label for rank column in results"
                  )
                }}
              </th>
              <th class="text-left">
                {{
                  $t(
                    "people:PersonProfile.CompetitionResultsSection.Award.label",
                    "Award"
                  )
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, i) in results"
              :key="item.id + '-' + index"
              :style="
                results.length - 1 == i ? 'border-bottom: 0!important;' : ''
              "
            >
              <td>
                <div>
                  {{ $dayjs.date(item.competition.startDate) }} |
                  {{ competitionDetails(item.competition) }}
                  {{ showSectionName(item.section) }}
                </div>
              </td>
              <td>
                <AppShowRegistrationHorses
                  v-if="personView"
                  component-name="AppShowHorseName"
                  :horses="item.registration.horses"
                  :registration-horses="item.registration.registrationHorses"
                />

                <AppShowPeopleList
                  v-else
                  :people="[item.registration.athletes[0]]"
                  show-name-only
                />
              </td>
              <td>
                <AppShowResultPreview
                  v-if="
                    !item.outsideOfCompetition ||
                    item.competition.event.discipline.codename == 'endurance'
                  "
                  :result="item"
                />
              </td>
              <td class="currency-display">
                <template v-if="item.outsideOfCompetition">
                  {{
                    $t(
                      "events:results.position.outsideOfCompetition.text",
                      "HC"
                    )
                  }}
                </template>
                <template v-else-if="item.teamResult">
                  {{ item.teamResult.rank }}
                  {{ $t("common:common.results.team.text") }}
                  <template v-if="!ignoreIndividualRank(item)">
                    / {{ item.rank }}
                    {{ $t("common:common.results.individualAbrv.text") }}
                  </template>
                </template>
                <template v-else>
                  {{ item.rank
                  }}{{
                    item.rank && item.competition.numberOfStarts
                      ? " / " + item.competition.numberOfStarts
                      : ""
                  }}
                </template>
              </td>
              <td class="currency-display">
                <template
                  v-if="
                    horseView ||
                    showPersonCompetitionAward(item, entityObject.id)
                  "
                >
                  <div v-if="item.prizeMoneyCents">
                    {{
                      masterCurrency(item.prizeMoneyCents, {
                        onlySymbol: true
                      })
                    }}
                  </div>
                  <div v-else-if="item.isPlaced">
                    <v-icon>$iconRosette</v-icon>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script setup>
// name: "AppPrintEntityCompetitionResultsTemplate"
import { ref, nextTick, computed } from "vue";
import { groupBy, uniq } from "lodash-es";
import { apolloClient } from "@/plugins/apollo";
import { gql } from "@apollo/client/core";

import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
import AppShowRegistrationHorses from "@AppShows/horse/AppShowRegistrationHorses.vue";
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import AppShowResultPreview from "@AppShows/AppShowResultPreview.vue";
import AppCompetitionResultExpanded from "@elements/AppCompetitionResultExpanded.vue";
import AppShowRankAndStarts from "@AppShows/AppShowRankAndStarts.vue";
import { useLazyLoadingQuery } from "@composables/lazy_loading_query";
import { showPersonCompetitionAward } from "@/components/person/showPersonCompetitionAward";
import {
  QUERY_FILTERS,
  QUERY_FILTER_VALUES
} from "@elements/AppCompetitionResultsFilter.vue";
import { useCurrency } from "@composables/masterdata/config";
import AppPrint from "@elements/AppPrint.vue";

import AppPrintBasicHorseDetails from "./AppPrintBasicHorseDetails.vue";

const props = defineProps({
  entityType: {
    type: String,
    required: true,
    validate(v) {
      return ["personId", "horseId"].includes(v);
    }
  },
  entityObject: {
    type: Object,
    required: true
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

const receivedResults = ref([]);
const loading = ref(false);
const printer = ref(null);

const personView = computed(() => {
  return props.entityType == "personId";
});
const horseView = computed(() => {
  return props.entityType == "horseId";
});

const { startLazyLoading } = useLazyLoadingQuery();
const { masterCurrency } = useCurrency();

async function print() {
  loading.value = true;

  const count = await apolloClient
    .query({
      query: gql`
      query resultsTotalForChunkPrint(
        $${props.entityType}: ID!
        ${QUERY_FILTERS}
      ) {
        results(
          ${props.entityType}: $${props.entityType}
          ${QUERY_FILTER_VALUES}
        )  {
          id
        }
      }
    `,
      notifyOnNetworkStatusChange: false,
      fetchPolicy: "no-cache",
      variables: {
        [props.entityType]: props.entityObject.id,
        ...props.filters
      }
    })
    .then(({ data }) => data.results.length);

  const query = gql`
    query resultsExportChunk(
      $${props.entityType}: ID!
      $offset: Int
      $limit: Int 
      $sortBy: String
      $sortDesc: Boolean
      ${QUERY_FILTERS}
    ) {
      results(
        ${props.entityType}: $${props.entityType}
        offset: $offset
        limit: $limit
        sortBy: $sortBy
        sortDesc: $sortDesc
        ${QUERY_FILTER_VALUES}
      ) @connection(key: "resultsExportChunkExportKey") {
        id
        rank
        isPlaced
        prizeMoneyCents
        sectionId
        section {
          id
          name
          shortName
        }
        outsideOfCompetition
        ${AppCompetitionResultExpanded.fragments.resultFields}
        competitionRuleId
        competitionRule @client {
          id
          name
        }
        ${AppShowRankAndStarts.fragments.rankStarts}
        ${AppShowResultPreview.fragments.result}
        resultDetails
        status
        teamResult {
          id
          rank
        }
        points {
          id
          ${AppCompetitionResultExpanded.fragments.pointFields}
          chartRule {
            id
            chart {
              id
              name
              ${AppCompetitionResultExpanded.fragments.athleteChart}
            }
          }
        }
        
        registration {
          id
          teamName
          groomNames
          carriageNumber
          drivingCarriageWidth
          vaultingLungerPerson {
            ${AppShowPeopleList.fragments.basicPerson}
          }
          athletes {
            ${AppShowPeopleList.fragments.basicPerson}
          }
          horses {
            ${AppShowRegistrationHorses.fragments.horseName}
          }
          registrationHorses {
            ${AppShowRegistrationHorses.fragments.registrationHorse}
          }
          ${AppCompetitionResultExpanded.fragments.registrationFields}
        }
        competition {
          id
          code
          name
          startDate
          description
          ${AppCompetitionResultExpanded.fragments.competition}
          difficultyLevelId
          difficultyLevel @client {
            id
            name
          }
          competitionLevelId
          competitionLevel @client {
            id
            name
          }
          eventcodeId
          eventcode @client {
            id
            name
          }
          eventId
          event {
            id
            disciplineId
            discipline @client {
              id
              name
              codename
            }
            ${AppShowEvent.fragments.event}
            countryId
            country @client {
              id
              name
              alpha2
              ioc
            }
            organiser {
              id
              name
            }
            regionId
            region @client {
              id
              name
            }
            place
          }
        }
      }
    }
  `;

  await startLazyLoading(query, {
    perQueryLimit: 100,
    fieldName: "results",
    totalCount: count,
    variables: {
      [props.entityType]: props.entityObject.id,
      sortBy: "competitionStartDate",
      sortDesc: true,
      ...props.filters
    }
  });

  await apolloClient
    .query({
      query,
      variables: {
        [props.entityType]: props.entityObject.id,
        sortBy: "competitionStartDate",
        sortDesc: true,
        ...props.filters
      },
      notifyOnNetworkStatusChange: false,
      fetchPolicy: "cache-only"
    })
    .then(({ data }) => {
      receivedResults.value = Object.values(
        groupBy(data.results ?? [], "competition.eventId")
      );
    });

  await nextTick();
  // if printer element does not exist, stop here
  if (!printer.value) {
    loading.value = false;
    return;
  }

  await printer.value.print();
  await nextTick();
  receivedResults.value = [];
  loading.value = false;
}

function ignoreIndividualRank(item) {
  return ["trec", "mountedgames"].includes(
    item.competition.event.discipline.name
  );
}

function competitionDetails(comp) {
  return [
    comp.code,
    comp.eventcode?.name,
    comp.competitionLevel?.name,
    comp.difficultyLevel?.displayName,
    comp.competitionRule?.name,
    uniq(comp.cupSeasons?.map(x => x.cup.name)?.join(" | ") ?? [])
  ]
    .filter(Boolean)
    .join(" | ");
}
function showSectionName(section) {
  if (!section) return "";
  return `(${section.shortName || section.name})`;
}
</script>
